import { MutationFunction } from '@apollo/client';
import { DataValue, graphql } from '@apollo/client/react/hoc';
import { flowRight as compose } from 'lodash';
import React from 'react';
import { FormattedMessage, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';

import { Ad } from 'src/app/components/Ad/Ad';
import { CommunityHeader } from 'src/app/components/Community/CommunityHeader';
import { GhostElement } from 'src/app/components/GhostElement/GhostElement';
import { MetaTags } from 'src/app/components/Meta/MetaTags';
import { ProgramItem } from 'src/app/components/Program/ProgramItem';
import { Sort } from 'src/app/components/Sort/Sort';
import { InfiniteVideoGridColumn } from 'src/app/components/Video/InfiniteVideoGridColumn';
import { AD_TYPE } from 'src/app/constants/ad';
import * as Queries from 'src/app/containers/Community/CommunityPage.queries';
import { EventTrack } from 'src/app/helpers/analytics';
import { infiniteGraphQL } from 'src/app/helpers/graphql';
import { withGraphQLLoader } from 'src/app/helpers/loader';
import { withIntl } from 'src/app/helpers/locale';
import { withLoginPopup } from 'src/app/helpers/login';
import { getWebsiteInfo } from 'src/app/helpers/schema';
import { onSortByChanges, parseSortByParams } from 'src/app/helpers/sort';
import { LocalStorage } from 'src/app/helpers/storage';
import { withStyles } from 'src/app/helpers/styles';
import { withToast } from 'src/app/helpers/toast';
import { onAddToWatchlistClick } from 'src/app/helpers/video';
import { InfiniteProvider } from 'src/app/models/graphql';
import { InjectedLoginPopupProps } from 'src/app/models/login';
import { InjectedToastProps } from 'src/app/models/toast';
import { AddWatchlistResult, Video } from 'src/app/models/video';

interface MyInjectedProps extends InfiniteProvider, InjectedIntlProps, InjectedToastProps, InjectedLoginPopupProps {
  addWatchlistMutation: MutationFunction<AddWatchlistResult>;
  data: DataValue<Queries.MyQueryResult>;
}

type MyOwnProps = RouteComponentProps;

type MyProps = MyOwnProps & MyInjectedProps;

const STORAGE_SORT_BY = new LocalStorage<string>('communityPageSortBy');

@compose([
  infiniteGraphQL<MyProps, DataValue<Queries.MyQueryResult>>(Queries.GRAPHQL_QUERY, {
    getPagedField: (result) => result.videos
  }, {
    options: (props: any) => {
      return {
        fetchPolicy: 'cache-first',
        variables: {
          sortBy: parseSortByParams(props.location.search, STORAGE_SORT_BY.getItem())
        }
      };
    }
  }),
  graphql(Queries.GRAPHQL_ADD_WATCHLIST_MUTATION, { name: 'addWatchlistMutation' })
])
@withGraphQLLoader()
@withIntl()
@withLoginPopup()
@withStyles({
  base: [require('./CommunityPage.base.scss')],
  desktop: [require('./CommunityPage.desktop.scss')]
})
@withToast()
class MyClassCommunityPage extends React.Component<MyProps> {
  constructor(props: MyProps) {
    super(props);

    this.onWatchlistButtonClick = this.onWatchlistButtonClick.bind(this);
  }

  render(): JSX.Element {
    const { data: { communityThumbnail, videos, user }, hasMoreEntries, history, loadMoreEntries, location } = this.props;
    const sortBy = parseSortByParams(location.search, STORAGE_SORT_BY.getItem());
    const videoItems = videos ? videos.items : [];

    return (
      <div className="bg-black-dark">
        <MetaTags
          description={{ id: 'meta.description.community' }}
          history={history}
          image={communityThumbnail}
          title={{ id: 'title.community' }}
          schema={[getWebsiteInfo()]}
        />

        <Ad
          adUnitName="community"
          className="d-flex jc-center bg-black py@md-20 pb-10"
          disableBillboard={true}
          type={AD_TYPE.MASTERHEAD}
        />

        <div className="overflow-hidden">
          <CommunityHeader
            className="mb-16 mb@md-42 container container-fluid@md"
            largeThumbnail={communityThumbnail}
          />
        </div>

        <div className="container container-fluid mt-16 mb-24 mt@md-0 mb@md-42">
          <FormattedMessage id="label.programs">
            {(message) => <div className="fs-16 fs@md-18 fw-500 ls-2 tt-uppercase mb-16 mb@md-20">{message}</div>}
          </FormattedMessage>

          <div className="container-grid mb-16 mb@md-18 mr-n4 mr@md-n30">
            {this.programItems}
            <GhostElement className="mr-4 mr@md-30 my-0 community-page__program-item" count={4} />
          </div>

          <div className="d-flex ai-center jc-between mb-18">
            <FormattedMessage id="label.videos">
              {(message) => <div className="fs-16 fs@md-18 fw-500 ls-2 tt-uppercase">{message}</div>}
            </FormattedMessage>

            <Sort sortBy={sortBy} handleSortByChanged={onSortByChanges(history, STORAGE_SORT_BY)} />
          </div>

          <InfiniteVideoGridColumn
            hasMore={hasMoreEntries}
            loadMore={loadMoreEntries}
            onWatchlistButtonClick={this.onWatchlistButtonClick}
            videos={videoItems}
            scrollLoading={3}
            user={user}
            eventLabel='community page'
          />
        </div>

        <Ad
          adUnitName="community"
          className="d-flex jc-center py@md-20"
          type={AD_TYPE.BOTTOM_FRAME}
        />
      </div>
    );
  }

  private get programItems(): JSX.Element[] | null {
    if (!this.props.data.programs) {
      return null;
    }

    return this.props.data.programs.items.map((item) => {
      return (
        <ProgramItem
          {...item}
          key={'program-item-' + item.id}
          className="mr-4 mr@md-30 mb-16 mb@md-42 community-page__program-item"
        />
      );
    });
  }

  private onWatchlistButtonClick(video: Video, pushEventTrack?: EventTrack) {
    return () => {
      const { addWatchlistMutation, data: { user }, openLoginPopup, showToast } = this.props;
      onAddToWatchlistClick(addWatchlistMutation, openLoginPopup, showToast, video, user, pushEventTrack);
    };
  }
}

export const CommunityPage = MyClassCommunityPage as React.ComponentClass<MyOwnProps>;
